import type React from "react";
import { useState } from "react";
import { twJoin } from "tailwind-merge";

import { useImageResolver } from "@/api/hooks/useImageResolver";
import { type FormImage, isImageUploaded } from "@/components/ImageInput/useImageInput";
import { ImagePlaceholder } from "@/components/ImagePlaceholder/ImagePlaceholder";
import { Button } from "@/design-system/Button";
import { preloadImage } from "@/helpers/image";
import { RuntimeAspectRatio } from "@/ui-adapters/RuntimeAspectRatio";

import { getAspectRatio } from "./helpers";

// Used when only a single image is present in a single post
type CommunityPostImageBaseProps = {
  image: FormImage;
  shouldLoad: boolean;
  onClick: (image: FormImage) => void;
};

export function CommunityPostSingleImage({ image, shouldLoad, onClick }: CommunityPostImageBaseProps): React.ReactNode {
  const [isLoading, setIsLoading] = useState(true);

  const resolveImage = useImageResolver();
  const isUploaded = isImageUploaded(image);

  const getSrc = (currImage: FormImage) => {
    if (!shouldLoad) return undefined;

    if (isImageUploaded(currImage)) {
      return resolveImage(currImage, "intrinsic");
    } else {
      return currImage.url;
    }
  };

  const getAltText = (currImage: FormImage) => {
    if ("description" in currImage) {
      return currImage.description ?? "";
    } else {
      return "";
    }
  };

  return (
    <Button
      variant="ghostSecondary"
      fullWidth="untilMd"
      onClick={() => onClick(image)}
      onMouseEnter={isUploaded ? () => preloadImage(image.url) : undefined}
    >
      <RuntimeAspectRatio aspectRatio={isUploaded ? getAspectRatio(image) : undefined}>
        {(aspectRatioProps) => (
          <span
            {...aspectRatioProps}
            className="relative max-h-media-display-compact w-full cursor-zoom-in overflow-hidden rounded-md bg-background-default-inverse @md:max-h-media-display-wide @md:w-auto"
          >
            {isLoading && <ImagePlaceholder image={isUploaded ? image : undefined} objectFit="contain" />}
            <img
              className={twJoin(
                "mx-auto block max-h-full max-w-full object-contain",
                isLoading ? "opacity-0" : "opacity-100 transition-opacity hover:opacity-90",
              )}
              src={getSrc(image)}
              alt={getAltText(image)}
              onLoad={() => setIsLoading(false)}
              onError={() => setIsLoading(false)}
              width={isUploaded ? image.metadata.width : undefined}
              height={isUploaded ? image.metadata.height : undefined}
            />
          </span>
        )}
      </RuntimeAspectRatio>
    </Button>
  );
}

// Used when multiple images are present in a single post
type CommunityPostMultiImageProps = CommunityPostImageBaseProps & {
  amountImagesAfter?: number;
  isLast?: boolean;
};

export function CommunityPostMultiImage({
  image,
  amountImagesAfter = 0,
  shouldLoad,
  isLast,
  onClick,
}: CommunityPostMultiImageProps): React.ReactNode {
  const [isLoading, setIsLoading] = useState(true);

  const resolveImage = useImageResolver();
  const isUploaded = isImageUploaded(image);

  // Empty `src` in combination with `width` and `height` preserves space to prevent CLS
  let src = "";
  if (shouldLoad) {
    src = isUploaded ? resolveImage(image, "lg") : image.url;
  }

  return (
    <Button
      variant="ghostSecondary"
      fullWidth
      onClick={() => onClick(image)}
      onMouseEnter={isUploaded ? () => preloadImage(image.url) : undefined}
    >
      <span className="relative aspect-square w-full cursor-zoom-in overflow-hidden rounded-lg border border-border-default">
        {isLoading && <ImagePlaceholder image={isUploaded ? image : undefined} />}
        {isLast && amountImagesAfter > 0 && (
          <span className="absolute top-0 left-0 flex size-full items-center justify-center bg-background-default-inverse/60 transition-colors hover:bg-background-default-inverse/80">
            <span className="text-heading-2 text-text-default-inverse">+{amountImagesAfter}</span>
          </span>
        )}
        {shouldLoad && (
          <img
            className={twJoin(
              "block size-full object-cover object-center",
              isLoading ? "opacity-0" : "opacity-100 transition-opacity hover:opacity-90",
            )}
            src={src}
            alt={"description" in image ? (image.description ?? "") : ""}
            width={isUploaded ? image.metadata.width : undefined}
            height={isUploaded ? image.metadata.height : undefined}
            onLoad={() => setIsLoading(false)}
            onError={() => setIsLoading(false)}
          />
        )}
      </span>
    </Button>
  );
}
